import * as XLSX from 'xlsx';

function excelColor(color) {
    const match = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
    if (!match || (match[4] !== undefined && Number(match[4]) === 0)) return null;

    return 'FF' + match.slice(1, 4)
        .map(channel => Number(channel).toString(16).padStart(2, '0'))
        .join('').toUpperCase();
}

function tableStyles(table) {
    return Array.from(table.rows, row => Array.from(row.cells, cell => {
        const font = { bold: cell.tagName === 'TH' };
        let background;

        for (let element = cell; element && element !== table.parentElement; element = element.parentElement) {
            const color = element.style.color
                ? excelColor(window.getComputedStyle(element).color)
                : null;
            if (!font.color && color) font.color = { argb: color };

            if (!background && element.style.backgroundColor) {
                background = excelColor(window.getComputedStyle(element).backgroundColor);
            }
        }

        const weight = cell.style.fontWeight || row.style.fontWeight;
        if (weight) font.bold = weight === 'bold' || Number(weight) >= 600;

        // Hourly reports highlight negative values inside a span rather than on the cell.
        const highlightedText = Array.from(cell.querySelectorAll('[style]')).find(element => (
            element.style.color && element.textContent.trim() === cell.textContent.trim()
        ));
        if (highlightedText) {
            const color = excelColor(window.getComputedStyle(highlightedText).color);
            if (color) font.color = { argb: color };
        }

        if (cell.querySelector('a[href]')) font.underline = true;

        return {
            span: cell.colSpan,
            font,
            alignment: {
                horizontal: cell.getAttribute('align') || cell.style.textAlign || (cell.tagName === 'TH' ? 'center' : undefined),
            },
            fill: background ? { type: 'pattern', pattern: 'solid', fgColor: { argb: background } } : undefined,
        };
    }));
}

export async function tableToExcel(table, name, filename) {
    try {
        let tableElement = typeof table === 'string' ? document.getElementById(table) : table;
        if (tableElement && tableElement.tagName !== 'TABLE') {
            tableElement = tableElement.querySelector('table');
        }

        if (!tableElement) {
            throw new Error('Table element not found. Cannot export to Excel.');
        }

        // Capture the table before awaiting the writer: some reports restore their UI after export.
        const tableClone = tableElement.cloneNode(true);
        const styles = tableStyles(tableElement);
        const bordered = Number(tableElement.getAttribute('border')) > 0;

        tableClone.querySelectorAll('th, td').forEach(cell => {
            const text = cell.textContent.trim();
            const legacyFormat = (cell.getAttribute('style') || '')
                .match(/mso-number-format\s*:\s*["']?([^;"']+)/i);

            if (legacyFormat) {
                cell.setAttribute('data-z', legacyFormat[1].replace(/\\@/g, '@'));
            }

            // Excel numbers cannot preserve leading zeros or IDs longer than 15 digits.
            if (
                cell.tagName === 'TH'
                || cell.getAttribute('data-z') === '@'
                || /^[+-]?0\d/.test(text)
                || /^[+-]?\d{16,}$/.test(text)
            ) {
                cell.setAttribute('data-t', 's');
            }
        });

        const source = XLSX.utils.table_to_sheet(tableClone, { cellDates: true });
        const { default: ExcelJS } = await import('exceljs/dist/exceljs.min.js');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(name || 'Worksheet');

        Object.keys(source).filter(address => !address.startsWith('!')).forEach(address => {
            const sourceCell = source[address];
            const cell = worksheet.getCell(address);
            cell.value = sourceCell.l
                ? { text: String(sourceCell.v ?? ''), hyperlink: sourceCell.l.Target }
                : sourceCell.v;

            if (sourceCell.z) cell.numFmt = sourceCell.z;
        });

        (source['!merges'] || []).forEach(merge => {
            worksheet.mergeCells(merge.s.r + 1, merge.s.c + 1, merge.e.r + 1, merge.e.c + 1);
        });

        styles.forEach((row, rowIndex) => {
            let column = 1;

            row.forEach(style => {
                let cell = worksheet.getCell(rowIndex + 1, column);
                while (cell.isMerged && cell.master.address !== cell.address) {
                    column += 1;
                    cell = worksheet.getCell(rowIndex + 1, column);
                }

                cell.font = style.font;
                cell.alignment = style.alignment;
                if (style.fill) cell.fill = style.fill;

                if (bordered) {
                    cell.border = {
                        top: { style: 'thin' },
                        bottom: { style: 'thin' },
                        left: { style: 'thin' },
                        right: { style: 'thin' },
                    };
                }

                column += style.span;
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        let binary = '';
        new Uint8Array(buffer).forEach(byte => {
            binary += String.fromCharCode(byte);
        });

        let link = document.getElementById('dlink');
        if (!link) {
            link = document.createElement('a');
            link.id = 'dlink';
            link.style.display = 'none';
            document.body.appendChild(link);
        }

        link.href = 'data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,' + window.btoa(binary);
        link.download = (filename || 'export').replace(/\.xlsx?$/i, '') + '.xlsx';
        link.click();
    } catch (error) {
        console.error('Error exporting to Excel:', error);
        window.alert('Error exporting to Excel: ' + error.message);
    }
}
